import classNames from "classnames";
import { ASPECT_RATIO, CONTENT_TYPES } from "constants/phase";
import { noDownloadStreams } from "constants/v2/streamConstants";
import { createPresignedDownloadLink } from "helpers";
import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
  selectClipListDetailsById,
  selectClipListEntityId,
  selectClipListPreviewDataDurationById,
  selectClipListPreviewUrlById,
  selectClipListStreamIdById,
} from "store/clipList/selectors";
import { selectContentImageTitleById, selectContentImageUrlById } from "store/core/contentImage/selectors";
import {
  selectContentVariantVariantContentId,
  selectContentVariantVariantEntityId,
  selectContentVariantVariantStreamId,
  selectContentVariantVariantTitle,
  selectContentVariantVariantVideoUrl,
} from "store/core/contentVariant/selectors";
import { selectSelectedEntityId } from "store/entity/selectors";
import {
  selectAppAdditionalPermissionsAllowDirectDownload,
  selectAppAdditionalPermissionsAllowPassThroughDownloadBySelectedUserId,
} from "store/entityConfig/selectors";
import { selectIllustoProjectDetails, selectIsStreamIndependent } from "store/illustoProject/selectors";
import { getCloudPreSignedURL, getDownloadStatus, publicDownloadVideo } from "store/publisher/actions";
import { selectSelectedUserId } from "store/user/selectors";

import { APP_VERSION } from "../../../../../constants/v2/appConstants";
import amplitude from "../../../../../externals/amplitude";
import {
  selectClipDetailsById,
  selectClipDisplayTitleById,
  selectClipEditedClipDataDurationById,
  selectClipEditedClipDataPreviewUrlById,
  selectClipsEntityId,
  selectClipStreamIdById,
  selectClipVideoUrlByAspectRatio,
} from "../../../../../store/clip/selectors";
import {
  selectClipVideoEntityIdById,
  selectClipVideoReferenceClipDisplayTitle,
  selectClipVideoStreamIdById,
  selectClipVideoVideoUrlById,
  selectIsStudioExportedClipVideo,
} from "../../../../../store/clipVideosList/selectors";
import { passThroughDownload } from "../../../../../store/common/action";
import { getDownloadVideo } from "../../../../../store/publisher/actions";
import { showNotification } from "../../../notification/notification";
import styles from "./style.module.css";

const ClipDownload = ({
  page,
  children,
  clipDetails,
  clipEntityId,
  isDisabled,
  clipVideoUrlByAspectRatio,
  getDownloadStatus,
  userId,
  clipStreamId,
  clipEditedClipDataPreviewUrl,
  isGeneratedVideoDownload = false,
  projectDetails,
  clipListPreviewUrl,
  clipListDetails,
  clipListPreviewDuration,
  clipListId,
  clipListStreamId,
  clipListEntityId,
  className = "",
  isPublic = false,
  contentType,
  variantStreamId,
  variantEntityId,
  variantVideoUrl,
  variantTitle,
  variantContentId,
  contentImageUrl,
  stopPropagation = false,
  selectedEntityId,
  getDownloadVideo,
  clipEditedClipDataDurationById,
  contentImageTitle,
  aspectRatio,
  isEditedClip = false,
  allowPassThroughDownload,
  passThroughDownload,
  publicDownloadVideo,
  contentId,
  clipVideoId,
  clipVideoVideoUrl,
  clipVideoTitle,
  clipVideoStreamId,
  clipVideoEntityId,
  dynamicCropVideoUrl,
  isStreamIndependent, // this should be true if it is an illusto clip and is independent of a stream
  isStudioExportedClipVideo, // when a clip/highlight having stream id gets exported in mxf format directly from illusto editor
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const contentIdToUse = useMemo(() => {
    if (contentType === CONTENT_TYPES.VARIANT) {
      return variantContentId;
    }

    if (contentType === CONTENT_TYPES.HIGHLIGHT) {
      return clipListId;
    }

    if (contentType === CONTENT_TYPES.CLIP) {
      return clipDetails?._id || clipDetails?.clipId;
    }

    if (contentType === CONTENT_TYPES.EXPORTED_CLIP) {
      return clipVideoId;
    }

    return clipListId || clipDetails?._id || clipDetails?.clipId || contentId;
  }, [contentType, clipListId, clipDetails?._id, clipDetails?.clipId, contentId, variantContentId, clipVideoId]);

  const contentTypeToUse = useMemo(() => {
    if (isStreamIndependent || isStudioExportedClipVideo) {
      return CONTENT_TYPES.STUDIO_PROJECT;
    }

    if (clipListId) {
      return CONTENT_TYPES.HIGHLIGHT;
    }

    return contentType || CONTENT_TYPES.CLIP;
  }, [contentType, clipListId, isStreamIndependent, isStudioExportedClipVideo]);

  const streamId = useMemo(() => {
    if (clipListId) {
      return clipListStreamId;
    }

    if (contentType === CONTENT_TYPES.VARIANT) {
      return variantStreamId;
    }

    if (contentType === CONTENT_TYPES.EXPORTED_CLIP) {
      return clipVideoStreamId;
    }

    return clipStreamId;
  }, [clipListStreamId, clipStreamId, clipListId, variantStreamId, contentType, clipVideoStreamId]);

  const entityId = useMemo(() => {
    if (clipListId) {
      return clipListEntityId;
    }

    if (contentType === CONTENT_TYPES.VARIANT) {
      return variantEntityId;
    }

    if (contentType === CONTENT_TYPES.EXPORTED_CLIP) {
      return clipVideoEntityId;
    }

    return clipEntityId;
  }, [clipListEntityId, clipEntityId, clipListId, variantEntityId, contentType, clipVideoEntityId]);

  const contentDownloadUrl = useMemo(() => {
    if (dynamicCropVideoUrl) {
      return dynamicCropVideoUrl;
    }

    if (isStreamIndependent) {
      return projectDetails?.url || clipVideoUrlByAspectRatio;
    }
    if (clipListId) {
      return clipListPreviewUrl;
    }
    if (isGeneratedVideoDownload) {
      return clipEditedClipDataPreviewUrl || clipVideoUrlByAspectRatio;
    }

    if (contentTypeToUse === CONTENT_TYPES.EXPORTED_CLIP || isStudioExportedClipVideo) {
      return clipVideoVideoUrl;
    }

    if (contentType === CONTENT_TYPES.VARIANT) {
      return variantVideoUrl;
    }

    if (contentType === CONTENT_TYPES.IMAGE) {
      return contentImageUrl;
    }

    return clipVideoUrlByAspectRatio;
  }, [
    dynamicCropVideoUrl,
    isStreamIndependent,
    clipListId,
    isGeneratedVideoDownload,
    contentTypeToUse,
    contentType,
    clipVideoUrlByAspectRatio,
    projectDetails?.url,
    clipListPreviewUrl,
    clipEditedClipDataPreviewUrl,
    clipVideoVideoUrl,
    variantVideoUrl,
    contentImageUrl,
    isStudioExportedClipVideo,
  ]);

  const downloadDuration = useMemo(() => {
    let duration;
    if (isGeneratedVideoDownload) {
      duration = clipListId ? clipListPreviewDuration : clipEditedClipDataDurationById;
    } else {
      duration = clipDetails?.duration || clipListPreviewDuration || clipListDetails?.generatedDuration;
    }

    return Number(duration); // making sure it is a number, in case it is a string
  }, [
    isGeneratedVideoDownload,
    clipDetails?.duration,
    clipListPreviewDuration,
    clipListDetails?.generatedDuration,
    clipListId,
    clipEditedClipDataDurationById,
  ]);

  const title = useMemo(() => {
    if (contentType === CONTENT_TYPES.IMAGE) {
      return contentImageTitle;
    }

    if (contentType === CONTENT_TYPES.VARIANT) {
      return variantTitle;
    }

    if (contentType === CONTENT_TYPES.EXPORTED_CLIP) {
      return clipVideoTitle;
    }

    if (isStreamIndependent) {
      return projectDetails?.projectTitle || clipDetails?.title || clipDetails?.episodeTitle;
    }

    return clipListId ? clipListDetails?.title : clipDetails?.episodeTitle || clipDetails?.title;
  }, [
    contentImageTitle,
    contentType,
    clipListId,
    clipListDetails,
    clipDetails,
    variantTitle,
    isStreamIndependent,
    projectDetails,
    clipVideoTitle,
  ]);

  const handleDownloadError = (error) => {
    showNotification({ type: "error", message: error?.message || "Something went wrong" });
  };

  const handleDownloadSuccess = useCallback(() => {
    try {
      if (!isPublic) {
        getDownloadStatus({ payload: { entityId: entityId || selectedEntityId, streamId } });
      }
      amplitude.logEvent("clip_downloaded", {
        page,
        version: APP_VERSION.V2,
      });

      amplitude.logEvent("content_downloaded", {
        content_id: clipDetails?._id || clipListId,
        content_type: contentType || CONTENT_TYPES.CLIP,
        duration: downloadDuration || clipDetails?.duration,
        origin: page,
        aspect_ratio: aspectRatio || clipDetails?.aspectRatio || clipListDetails?.aspectRatio || "Unknown",
        version: APP_VERSION.V2,
        page,
      });
    } catch (error) {
      console.error(error);
    }
  }, [
    isPublic,
    page,
    clipDetails?._id,
    clipDetails?.duration,
    clipDetails?.aspectRatio,
    clipListId,
    contentType,
    downloadDuration,
    aspectRatio,
    clipListDetails?.aspectRatio,
    getDownloadStatus,
    entityId,
    selectedEntityId,
    streamId,
  ]);

  const downloadContentFromUrl = useCallback(
    async (contentDownloadUrl) => {
      try {
        let payloadData = {
          entityId: selectedEntityId || entityId,
          url: contentDownloadUrl,
          streamId: streamId === "undefined" ? undefined : streamId, // As streamId can be string "undefined" when we are extracting it from query params in public routes
          duration: downloadDuration || 0,
          contentId: String(contentIdToUse),
          contentType: contentTypeToUse,
          userId,
          ...(title ? { title } : {}),
          aspectRatio: aspectRatio || ASPECT_RATIO["16_9"], // set default aspect ratio to 16:9
          ...(isEditedClip ? { isEditedClip } : {}),
        };

        let response;

        if (isPublic) {
          /*
            NOTE:
            Public download is for downloads from public pages: 
            - /preview-video
          */
          response = await publicDownloadVideo({ payload: payloadData });
        } else {
          /*
            NOTE:
            Pass through download was enabled through magapi because a client was unable to 
            connect to publisher service due to some network settings on their side
          */
          response = await (allowPassThroughDownload
            ? passThroughDownload({ payload: payloadData })
            : getDownloadVideo({ payload: payloadData }));
        }

        if (response?.payload?.data?.url) {
          // Now use the url received from the api to download the content
          createPresignedDownloadLink(response?.payload?.data?.url, false, title || "");
          showNotification({ message: "Download started" });
          handleDownloadSuccess();
        } else {
          showNotification({ type: "error", message: response?.payload?.data?.message || "Something went wrong" });
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      selectedEntityId,
      entityId,
      streamId,
      downloadDuration,
      contentIdToUse,
      contentTypeToUse,
      userId,
      title,
      aspectRatio,
      isEditedClip,
      publicDownloadVideo,
      allowPassThroughDownload,
      passThroughDownload,
      getDownloadVideo,
      isPublic,
      handleDownloadSuccess,
    ],
  );

  const onClick = useCallback(
    async (e) => {
      if (stopPropagation) {
        e.stopPropagation();
      }
      if (!isLoading && !isDisabled) {
        setIsLoading(true);
        try {
          amplitude.logEvent("download_clip_triggered", {
            page,
            version: APP_VERSION.V2,
          });

          await downloadContentFromUrl(contentDownloadUrl);
        } catch (error) {
          handleDownloadError(error);
        } finally {
          setIsLoading(false);
        }
      }
    },
    [stopPropagation, isLoading, isDisabled, page, downloadContentFromUrl, contentDownloadUrl],
  );

  return (
    <div
      className={classNames(styles.container, className, {
        [styles.disabled]: isLoading || isDisabled || noDownloadStreams.includes(clipStreamId),
      })}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  clipDetails: selectClipDetailsById,
  clipDisplayTitle: selectClipDisplayTitleById,
  clipEntityId: selectClipsEntityId,
  clipVideoUrlByAspectRatio: selectClipVideoUrlByAspectRatio,
  userId: selectSelectedUserId,
  clipStreamId: selectClipStreamIdById,
  clipEditedClipDataPreviewUrl: selectClipEditedClipDataPreviewUrlById,
  isStreamIndependent: selectIsStreamIndependent,
  projectDetails: selectIllustoProjectDetails,
  clipListPreviewUrl: selectClipListPreviewUrlById,
  clipListDetails: selectClipListDetailsById,
  clipListPreviewDuration: selectClipListPreviewDataDurationById,
  clipListStreamId: selectClipListStreamIdById,
  clipListEntityId: selectClipListEntityId,
  variantStreamId: selectContentVariantVariantStreamId,
  variantEntityId: selectContentVariantVariantEntityId,
  variantVideoUrl: selectContentVariantVariantVideoUrl,
  variantTitle: selectContentVariantVariantTitle,
  variantContentId: selectContentVariantVariantContentId,
  contentImageUrl: selectContentImageUrlById,
  clipEditedClipDataDurationById: selectClipEditedClipDataDurationById,
  selectedEntityId: selectSelectedEntityId,
  contentImageTitle: selectContentImageTitleById,
  clipVideoVideoUrl: selectClipVideoVideoUrlById,
  clipVideoTitle: selectClipVideoReferenceClipDisplayTitle,
  clipVideoStreamId: selectClipVideoStreamIdById,
  clipVideoEntityId: selectClipVideoEntityIdById,
  allowDirectDownload: selectAppAdditionalPermissionsAllowDirectDownload,
  allowPassThroughDownload: selectAppAdditionalPermissionsAllowPassThroughDownloadBySelectedUserId,
  isStudioExportedClipVideo: selectIsStudioExportedClipVideo,
});

const mapDispatchToProps = {
  getDownloadVideo,
  getCloudPreSignedURL,
  publicDownloadVideo,
  passThroughDownload,
  getDownloadStatus,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClipDownload);
